import { FILTER_GROUPS, filterGroupFor, filterPriority } from './filterGrouping';
import { humanise } from './format';
import { switchesInTitles } from './lensState';
import type { FilterGroupLayout, LensDocument, SqlParam } from './types';

export type LayoutControl = { id: string; label: string; kind: 'Filter' | 'Input' };

export function railParamNames(document: LensDocument): Array<string> {
  const titles = switchesInTitles(document);
  return Object.entries(document.params)
    .filter(
      ([name, param]) => 'sql' in param && param.options.length > 0 && (!titles.has(name) || param.role === 'grain'),
    )
    .map(([name]) => name);
}

export function layoutControls(document: LensDocument): Array<LayoutControl> {
  return [
    ...railParamNames(document).map((name) => ({
      id: `param:${name}`,
      label: document.params[name].label ?? humanise(name),
      kind: 'Input' as const,
    })),
    ...document.filters.map((filter) => ({
      id: `filter:${filter.column}`,
      label: filter.label ?? humanise(filter.column),
      kind: 'Filter' as const,
    })),
  ];
}

/** Suggested groups are a starting point. Saved author order always wins. */
export function automaticFilterGroups(document: LensDocument): Array<FilterGroupLayout> {
  const controls = layoutControls(document);
  return FILTER_GROUPS.flatMap((label) => {
    const members = controls
      .filter((control) => {
        const [kind, key] = control.id.split(':');
        return (
          filterGroupFor(key, control.label, kind === 'param' ? (document.params[key] as SqlParam).role : undefined) ===
          label
        );
      })
      .sort((a, b) => filterPriority(label, a.label) - filterPriority(label, b.label));
    return members.length
      ? [
          {
            id: label.toLowerCase().replace(/ /g, '-'),
            label,
            controls: members.map((control) => control.id),
            collapsed: label !== 'Customer',
          },
        ]
      : [];
  });
}

/** Missing/new controls remain discoverable; stale references never hide valid controls. */
export function resolvedFilterGroups(document: LensDocument): Array<FilterGroupLayout> {
  const available = new Set(layoutControls(document).map((control) => control.id));
  const assigned = new Set<string>();
  const groups = (document.display.filterGroups ?? automaticFilterGroups(document)).map((group) => ({
    ...group,
    controls: group.controls.filter((id) => {
      if (!available.has(id) || assigned.has(id)) return false;
      assigned.add(id);
      return true;
    }),
  }));
  const unassigned = [...available].filter((id) => !assigned.has(id));
  const ungrouped = groups.find((group) => group.id === 'ungrouped');
  if (ungrouped) ungrouped.controls.push(...unassigned);
  else groups.push({ id: 'ungrouped', label: 'Ungrouped', controls: unassigned, collapsed: true });
  return groups;
}

export function moveLayoutItem<T>(items: Array<T>, from: number, to: number): Array<T> {
  const next = [...items];
  if (from < 0 || to < 0 || from >= items.length || to >= items.length) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
